import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@pdfking/components"
import { GroupBy, Items } from "@pdfking/tfunc"
import type { JSX } from "preact"

import { DocExampleView } from "../components/DocExampleView"
import { Layout } from "../components/Layout"
import { PackageName } from "../docs/PackageName"
import type { PackageSlug } from "../docs/Packages"
import { Link, Navigate } from "../router"
import type { DocsEntry, DocsPage } from "../types/DocsPage"

// marked's html carries no classes: style it from the container.
const PROSE =
  "flex flex-col gap-3 text-sm leading-relaxed [&_a]:underline [&_code]:font-mono [&_h1]:text-3xl [&_h1]:font-semibold [&_h2]:mt-4 [&_h2]:text-xl [&_h2]:font-semibold [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_ul]:list-disc [&_ul]:pl-6"

// Exports by their folder under public/: "./public/functions/Range" is in
// "functions".
const Sections = (exports: DocsEntry[]) =>
  Items(GroupBy(exports, entry => entry.module.split("/")[2] ?? ""))

// A link in a page's html (see DocsMarkdown) is not a Link: navigate it in
// place all the same.
const FollowDocsLink = (event: JSX.TargetedMouseEvent<HTMLElement>): void => {
  const anchor = (event.target as Element).closest<HTMLAnchorElement>("a[data-package]")
  if (!anchor || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return
  event.preventDefault()
  Navigate(
    "docs",
    { package: anchor.dataset.package as PackageSlug, page: anchor.dataset.page },
    anchor.dataset.fragment
  )
}

const Entry = ({ entry }: { entry: DocsEntry }) => (
  <div className="flex scroll-mt-4 flex-col gap-3" id={entry.name}>
    <h3 className="font-mono text-lg font-medium">{entry.name}</h3>
    <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-sm">
      {entry.signature}
    </pre>
    <div className={PROSE} dangerouslySetInnerHTML={{ __html: entry.doc }} />
    {entry.examples.map((example, index) => (
      <DocExampleView example={example} key={index} />
    ))}
  </div>
)

/** A docs page: the tree of every package's pages in the sidebar's panel, and
 * the page — a guide's html, or a package's Runtime or Types. */
export const Docs = ({ page }: { page: DocsPage }) => (
  <Layout
    active="docs"
    panel={
      <SidebarGroup>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={!page.package}>
              <Link data={{ package: undefined, page: undefined }} name="docs">
                Overview
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {page.tree.map(({ slug, pages }) => (
            <SidebarMenuItem key={slug}>
              <SidebarMenuButton asChild isActive={slug === page.package && !page.page}>
                <Link data={{ package: slug, page: undefined }} name="docs">
                  {PackageName(slug)}
                </Link>
              </SidebarMenuButton>
              <SidebarMenuSub>
                {pages.map(link => (
                  <SidebarMenuSubItem key={link.page}>
                    <SidebarMenuSubButton
                      asChild
                      isActive={slug === page.package && link.page === page.page}
                    >
                      <Link data={{ package: slug, page: link.page }} name="docs">
                        {link.title}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    }
  >
    <article className="flex min-w-0 flex-1 flex-col gap-10 px-8 py-10" onClick={FollowDocsLink}>
      {page.exports.length ? (
        <>
          <div className="flex flex-col gap-1">
            {page.package && (
              <span className="font-mono text-sm text-muted-foreground">
                {PackageName(page.package)}
              </span>
            )}
            <h1 className="text-3xl font-semibold">{page.title}</h1>
          </div>
          {Sections(page.exports).map(([section, entries]) => (
            <section className="flex flex-col gap-8" key={section}>
              <h2 className="border-b pb-2 text-xl font-semibold capitalize">{section}</h2>
              {entries.map(entry => (
                <Entry entry={entry} key={entry.name} />
              ))}
            </section>
          ))}
        </>
      ) : (
        <div className={PROSE} dangerouslySetInnerHTML={{ __html: page.html }} />
      )}
    </article>
  </Layout>
)
